<template>
    <div class="sub-account-page rightPage">
        <!-- 搜索栏 -->
        <div class="rightHeader">
            <v-search
                :formContent="formContent"
                :SearchForm="SearchForm"
                :inline="true"
                :optareBtn="optareBtn"
                @resetFn="resetFn"
                @searchFn="getListFn"
                @confirmFn="getListFn"
                @addFn="openCreateDialog"
                @disableFn="batchDisable"
                @enableFn="batchEnable"
            />
        </div>

        <!-- UAM Account 列表 -->
        <div class="componentPage">
            <el-table
                v-loading="loading"
                :data="tableData"
                stripe
                style="width: 100%"
                :height="tableHeight"
                :empty-text="$t('account_redesign.no_data')"
                @selection-change="handleSelectionChange"
            >
                <el-table-column type="selection" width="55" />
                <el-table-column
                    :label="$t('account_redesign.uam_account')"
                    prop="account"
                    min-width="120"
                    show-overflow-tooltip
                ></el-table-column>
                <el-table-column
                    :label="$t('account_redesign.display_name')"
                    prop="username"
                    min-width="120"
                    show-overflow-tooltip
                >
                    <template #default="{ row }">
                        {{ row.username || '-' }}
                    </template>
                </el-table-column>
                <el-table-column
                    :label="$t('account_redesign.company')"
                    prop="customerName"
                    min-width="140"
                    show-overflow-tooltip
                >
                    <template #default="{ row }">
                        {{ row.customerName && row.customerName !== '---' ? row.customerName : '-' }}
                    </template>
                </el-table-column>
                <el-table-column
                    :label="$t('account_redesign.status')"
                    prop="status"
                    width="100"
                    align="center"
                >
                    <template #default="{ row }">
                        <i :class="getStatusIcon(row.status).icon" :style="{ color: getStatusIcon(row.status).color, fontSize: '18px' }"></i>
                    </template>
                </el-table-column>
                <el-table-column
                    :label="$t('public.phone_num')"
                    prop="mobile"
                    min-width="120"
                    show-overflow-tooltip
                ></el-table-column>
                <el-table-column
                    :label="$t('public.email')"
                    prop="email"
                    min-width="200"
                    show-overflow-tooltip
                ></el-table-column>
                <el-table-column
                    :label="$t('account_redesign.create_time')"
                    prop="createTime"
                    min-width="170"
                >
                    <template #default="{ row }">
                        {{ formatTime(row.createTime) }}
                    </template>
                </el-table-column>
                <el-table-column
                    :label="$t('account_redesign.actions')"
                    width="140"
                    fixed="right"
                    align="center"
                >
                    <template #default="{ row }">
                        <el-button v-if="row.status === 1" type="text" icon="el-icon-edit" size="small" :title="$t('account_redesign.edit_uam_account')" @click="openEditDialog(row)"></el-button>
                        <el-button v-if="row.status !== 1" type="text" icon="el-icon-refresh" size="small" :title="$t('account_redesign.reinvite')" @click="handleReinvite(row)"></el-button>
                        <el-button type="text" icon="el-icon-delete" size="small" :title="$t('account_redesign.delete')" class="delete-btn" @click="handleDelete(row)"></el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页 -->
            <Pagination class="pagination" :total="total" :page.sync="SearchForm.iDisplayStart"
                        :limit.sync="SearchForm.iDisplayLength" @pagination="getListFn"></Pagination>
        </div>

        <!-- 新增向导 -->
        <WizardDialog :isShow.sync="wizardVisible" :statusArray="wizardStatusArray" :roleTypeArray="roleTypeArray" activeUserType="ENTERPRISE" />

        <!-- 编辑UAM账号弹窗 -->
        <EditAccountDialog :isShow.sync="editDialogVisible" :editRow="editRow" />

        <!-- 删除确认弹窗 -->
        <modal
            :title="$t('account_redesign.delete_confirm_title')"
            :visible.sync="deleteDialogVisible"
            width="500px"
            :close-on-click-modal="false"
            append-to-body
        >
            <div class="delete-confirm-body">
                <p class="delete-confirm-tip">{{ $t('account_redesign.delete_confirm_tip') }}</p>
                <el-form label-width="120px" label-position="right">
                    <el-form-item :label="$t('account_redesign.delete_confirm_account')">
                        <span>{{ deleteRow.account }}</span>
                    </el-form-item>
                    <el-form-item :label="$t('account_redesign.delete_confirm_display_name')">
                        <el-input
                            v-model="deleteInputName"
                            :placeholder="$t('account_redesign.delete_confirm_placeholder')"
                            style="width: 100%"
                        ></el-input>
                    </el-form-item>
                </el-form>
            </div>
            <div slot="footer" class="dialog-footer-gap">
                <el-button type="primary" @click="deleteDialogVisible = false">{{ $t('account_redesign.close') }}</el-button>
                <el-button type="primary" :loading="deleteLoading" @click="confirmDelete">{{ $t('public.btn_ok') }}</el-button>
            </div>
        </modal>

        <!-- 重新邀请成功弹窗 -->
        <modal
            :title="$t('account_redesign.reinvite_success_title')"
            :visible.sync="reinviteSuccessVisible"
            width="600px"
            :close-on-click-modal="false"
            append-to-body
        >
            <div class="success-dialog-body">
                <p class="success-body-text">{{ $t('account_redesign.reinvite_success_body') }}</p>
                <p class="success-warning-text">{{ $t('account_redesign.success_create_warning') }}</p>
                <p class="success-choose-text">{{ $t('account_redesign.success_create_choose') }}</p>
                <p class="success-desc-text">{{ $t('account_redesign.success_create_csv_desc') }}</p>
                <p class="success-desc-text">{{ $t('account_redesign.success_create_show_desc') }}</p>
                <div v-if="passwordVisible" class="password-display">
                    <span class="password-label">{{ $t('account_redesign.csv_password') }}：</span>
                    <span class="password-value">{{ reinvitePassword }}</span>
                </div>
                <p class="success-security-text">{{ $t('account_redesign.success_create_security') }}</p>
                <p class="success-warning-text">{{ $t('account_redesign.success_create_48h_warning') }}</p>
            </div>
            <div slot="footer">
                <el-button type="primary" @click="downloadReinviteCsv">{{ $t('account_redesign.download_csv_file') }}</el-button>
                <el-button type="primary" @click="passwordVisible = !passwordVisible">{{ passwordVisible ? $t('account_redesign.hide_password') : $t('account_redesign.show_password') }}</el-button>
                <el-button type="primary" @click="reinviteSuccessVisible = false">{{ $t('account_redesign.close') }}</el-button>
            </div>
        </modal>
    </div>
</template>

<script>
import {
    listSubAccounts,
    deleteSubAccount,
    reinviteSubAccount,
    batchUpdateStatus
} from '@/api/modules/account.js'
import WizardDialog from '@/pages/usercontrol/components/WizardDialog.vue'
import EditAccountDialog from '@/pages/usercontrol/components/EditAccountDialog.vue'

export default {
    name: 'SubAccountManagement',
    components: { WizardDialog, EditAccountDialog },
    data() {
        return {
            loading: false,
            tableData: [],
            selectedRows: [],
            tableHeight: 'calc(100% - 60px)',
            total: 0,
            // v-search 搜索配置
            SearchForm: {
                account: null,
                username: null,
                status: null,
                iDisplayStart: 0,
                iDisplayLength: 15
            },
            // 4 种状态 + Please Select（搜索栏用）
            statusArray: [
                { value: '', label: 'public.please', icon: 'el-icon-thumb' },
                { value: 1, label: 'account_redesign.enable', icon: 'el-icon-circle-check', color: '#67c23a' },
                { value: 0, label: 'account_redesign.disable', icon: 'el-icon-circle-close', color: '#f56c6c' },
                { value: 2, label: 'account_redesign.pending', icon: 'el-icon-time', color: '#e6a23c' },
                { value: 3, label: 'account_redesign.expire', icon: 'el-icon-lock', color: '#909399' }
            ],
            // WizardDialog 用的 statusArray（不含 Please Select）
            wizardStatusArray: [
                { value: 1, label: 'account_redesign.enable' },
                { value: 0, label: 'account_redesign.disable' },
                { value: 2, label: 'account_redesign.pending' },
                { value: 3, label: 'account_redesign.expire' }
            ],
            roleTypeArray: [
                { value: 0, label: 'user.role_type_enterprise' },
                { value: 2, label: 'user.role_type_platform' }
            ],
            optareBtn: [
                { id: 1, icon: '', text: 'public.btn_new', method: 'addFn' },
                { id: 2, icon: '', text: 'account_redesign.batch_disable', method: 'disableFn' },
                { id: 3, icon: '', text: 'account_redesign.batch_enable', method: 'enableFn' }
            ],
            // 向导弹窗
            wizardVisible: false,
            // 编辑UAM账号弹窗
            editDialogVisible: false,
            editRow: null,
            // 删除确认弹窗
            deleteDialogVisible: false,
            deleteRow: {},
            deleteInputName: '',
            deleteLoading: false,
            // 重新邀请成功弹窗
            reinviteSuccessVisible: false,
            reinvitePassword: '',
            reinviteAccount: '',
            reinviteCustomerId: '',
            passwordVisible: false
        }
    },
    computed: {
        customerId() {
            return localStorage.getItem('customerId') || ''
        },
        formContent() {
            return [
                { id: 1, text: 'account_redesign.uam_account', type: 'input', paramsName: 'account', isChooze: true },
                { id: 2, text: 'account_redesign.display_name', type: 'input', paramsName: 'username', isChooze: false },
                { id: 3, text: 'account_redesign.status', type: 'select', paramsName: 'status', selectParam: this.statusArray, isChooze: false }
            ]
        }
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        /** 根据 status 返回 icon 和颜色 */
        getStatusIcon(status) {
            const map = {
                1: { icon: 'el-icon-circle-check', color: '#67c23a' },
                0: { icon: 'el-icon-circle-close', color: '#f56c6c' },
                2: { icon: 'el-icon-time', color: '#e6a23c' },
                3: { icon: 'el-icon-lock', color: '#909399' }
            }
            return map[status] || { icon: 'el-icon-circle-close', color: '#f56c6c' }
        },
        async fetchData() {
            this.loading = true
            try {
                const res = await listSubAccounts({
                    start: this.SearchForm.iDisplayStart,
                    size: this.SearchForm.iDisplayLength,
                    customerId: this.customerId,
                    account: this.SearchForm.account || undefined,
                    name: this.SearchForm.username || undefined,
                    accountStatus: this.SearchForm.status,
                    userType: 'ENTERPRISE'
                })
                if (res && res.state && res.state.code === 200) {
                    this.tableData = res.result || []
                    this.total = res.pageInfo ? res.pageInfo.total : 0
                } else {
                    this.tableData = []
                    this.total = 0
                }
            } catch (e) {
                console.error('fetch sub accounts error:', e)
                this.tableData = []
                this.total = 0
            } finally {
                this.loading = false
            }
        },
        getListFn() {
            this.fetchData()
        },
        resetFn() {
            this.SearchForm = {
                account: null,
                username: null,
                status: null,
                iDisplayStart: 0,
                iDisplayLength: 15
            }
            this.fetchData()
        },
        handleSelectionChange(rows) {
            this.selectedRows = rows
        },
        // 新增 - 打开向导
        openCreateDialog() {
            this.wizardVisible = true
        },
        // 批量禁用
        async batchDisable() {
            if (this.selectedRows.length === 0) {
                return this.$message.warning(this.$t('public.least_one_data'))
            }
            try {
                await this.$confirm(
                    this.$t('account_redesign.batch_disable_tip'),
                    this.$t('account_redesign.batch_disable'),
                    { type: 'warning' }
                )
            } catch (e) {
                return
            }
            try {
                const userIds = this.selectedRows.map(r => r.id)
                const res = await batchUpdateStatus(userIds, 0)
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('public.tip_operate_succ'))
                    this.fetchData()
                } else {
                    this.$message.error((res && res.state && res.state.msg) || this.$t('public.tip_operate_fail'))
                }
            } catch (e) {
                this.$message.error(this.$t('public.tip_operate_fail'))
            }
        },
        // 批量启用
        async batchEnable() {
            if (this.selectedRows.length === 0) {
                return this.$message.warning(this.$t('public.least_one_data'))
            }
            try {
                await this.$confirm(
                    this.$t('account_redesign.batch_enable_tip'),
                    this.$t('account_redesign.batch_enable'),
                    { type: 'warning' }
                )
            } catch (e) {
                return
            }
            try {
                const userIds = this.selectedRows.map(r => r.id)
                const res = await batchUpdateStatus(userIds, 1)
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('public.tip_operate_succ'))
                    this.fetchData()
                } else {
                    this.$message.error((res && res.state && res.state.msg) || this.$t('public.tip_operate_fail'))
                }
            } catch (e) {
                this.$message.error(this.$t('public.tip_operate_fail'))
            }
        },
        // 编辑UAM账号
        openEditDialog(row) {
            this.editRow = row
            this.editDialogVisible = true
        },
        // 重新发送邀请
        async handleReinvite(row) {
            try {
                await this.$confirm(
                    this.$t('account_redesign.tip_reinvite'),
                    this.$t('account_redesign.reinvite'),
                    { type: 'warning' }
                )
            } catch (e) {
                return
            }
            try {
                const res = await reinviteSubAccount(row.id)
                if (res && res.state && res.state.code === 200) {
                    const result = res.result || {}
                    // 兼容 snake_case（后端 @SnakeCaseResponse 返回 initial_password）
                    this.reinvitePassword = result.initialPassword || ''
                    this.reinviteAccount = row.account || ''
                    this.reinviteCustomerId = row.customerId || ''
                    this.passwordVisible = false
                    this.reinviteSuccessVisible = true
                    this.fetchData()
                } else {
                    this.$message.error((res && res.state && res.state.msg) || this.$t('account_redesign.reinvite_failed'))
                }
            } catch (e) {
                console.error('reinvite error:', e)
                const msg = (e && e.response && e.response.data && e.response.data.state && e.response.data.state.msg)
                    || (e && e.data && e.data.state && e.data.state.msg)
                    || (e && e.state && e.state.msg)
                    || this.$t('account_redesign.reinvite_failed')
                this.$message.error(msg)
            }
        },
        // 下载重新邀请的 CSV
        downloadReinviteCsv() {
            let loginUrl = window.location.origin + '/login'
            if (this.reinviteCustomerId && this.reinviteCustomerId !== '1') {
                loginUrl += '?companyCode=' + encodeURIComponent(this.reinviteCustomerId)
            }
            const header = `${this.$t('account_redesign.csv_username')},${this.$t('account_redesign.csv_password')},${this.$t('account_redesign.csv_login_url')}`
            const row = `${this.reinviteAccount},${this.reinvitePassword},${loginUrl}`
            const csvContent = header + '\n' + row
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `uam_account_${this.reinviteAccount}.csv`
            link.click()
            URL.revokeObjectURL(link.href)
        },
        // 删除子账号 - 打开确认弹窗
        handleDelete(row) {
            this.deleteRow = row
            this.deleteInputName = ''
            this.deleteDialogVisible = true
        },
        // 确认删除
        async confirmDelete() {
            const expectedName = (this.deleteRow.username || '').trim()
            const inputName = (this.deleteInputName || '').trim()
            if (!inputName) {
                return this.$message.warning(this.$t('account_redesign.delete_confirm_placeholder'))
            }
            if (inputName !== expectedName) {
                return this.$message.error(this.$t('account_redesign.delete_confirm_mismatch'))
            }
            this.deleteLoading = true
            try {
                const res = await deleteSubAccount(this.deleteRow.id)
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('public.tip_del_succ'))
                    this.deleteDialogVisible = false
                    this.fetchData()
                }
            } catch (e) {
                console.error('delete sub account error:', e)
            } finally {
                this.deleteLoading = false
            }
        },
        /** 时间戳转 YYYY-MM-dd HH:mm:ss */
        formatTime(time) {
            if (!time) return '-'
            const d = new Date(time)
            const pad = (n) => String(n).padStart(2, '0')
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        }
    }
}
</script>

<style lang="scss" scoped>
.sub-account-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 10px 15px 0 15px;
    box-sizing: border-box;

    .rightHeader {
        position: relative;
        flex-shrink: 0;
    }

    .componentPage {
        flex: 1;
        overflow: auto;

        .delete-btn {
            color: #f56c6c;
        }

        .pagination {
            padding-top: 0;
            padding-right: 15px !important;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
    }
}

/* 删除确认弹窗 */
.delete-confirm-body {
    .delete-confirm-tip {
        color: #e6a23c;
        font-weight: bold;
        margin-bottom: 20px;
    }
}

/* 弹窗底部按钮居中 + 间距 */
.dialog-footer-gap {
    display: flex;
    justify-content: center;
    gap: 12px;
}

/* 重新邀请成功弹窗 */
.success-dialog-body {
    line-height: 1.8;
    .success-body-text {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 12px;
    }
    .success-warning-text {
        color: #e6a23c;
        font-weight: bold;
        margin-bottom: 12px;
    }
    .success-choose-text {
        margin-bottom: 8px;
    }
    .success-desc-text {
        color: #606266;
        margin-bottom: 4px;
    }
    .password-display {
        margin: 16px 0;
        padding: 10px 16px;
        background: #f0f9eb;
        border: 1px solid #e1f3d8;
        border-radius: 4px;
        .password-label {
            font-weight: bold;
        }
        .password-value {
            font-family: monospace;
            font-size: 16px;
            color: var(--primary-color);
        }
    }
    .success-security-text {
        color: #909399;
        font-size: 13px;
        margin-top: 12px;
    }
}
</style>
